import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, Brain, Scale, Users, CheckCircle, AlertTriangle } from 'lucide-react';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>À propos | Anti-Recouvrement — Notre mission, notre équipe, notre méthode</title>
        <meta
          name="description"
          content="Anti-Recouvrement est une plateforme française d'analyse de dossiers de recouvrement par intelligence artificielle. Découvrez notre mission, notre méthode et nos partenaires avocats."
        />
        <meta property="og:title" content="À propos d'Anti-Recouvrement" />
        <meta property="og:description" content="Plateforme française d'analyse de dossiers de recouvrement par IA — mission, méthode, équipe." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.anti-recouvrement.com/a-propos" />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
        <Header />

        {/* Hero */}
        <section className="py-16 bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6" style={{ color: '#1e3a8a' }}>
              Notre mission : rendre le droit du recouvrement <em>accessible</em>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto">
              Chaque année, des centaines de milliers de personnes en France paient des
              dettes qu'elles ne devraient plus payer — par méconnaissance de leurs droits.
              Anti-Recouvrement existe pour inverser cette asymétrie.
            </p>
          </div>
        </section>

        {/* Mission détaillée */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            <div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Pourquoi nous existons</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Le droit français protège les débiteurs : <strong>prescription de 2 ans</strong> sur la plupart des
                créances à la consommation, vices de forme stricts sur les actes d'huissier, exigences procédurales
                précises pour qu'un titre exécutoire soit valide. Ces protections existent — mais elles ne servent à
                rien si les gens concernés ne savent pas qu'elles existent.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Les cabinets de recouvrement comptent souvent sur cette asymétrie. Notre service la corrige : nous
                analysons gratuitement votre dossier avec une intelligence artificielle entraînée sur la jurisprudence
                française, et nous vous disons honnêtement où vous en êtes.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Comment ça marche</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <Brain size={24} className="text-blue-700" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900">1. Analyse IA</h3>
                  <p className="text-gray-700">
                    Vous déposez votre courrier de recouvrement. Notre IA lit les documents (OCR), extrait les éléments
                    juridiques clés (dates, montants, créancier, type d'acte) et applique le droit français.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <Scale size={24} className="text-green-700" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900">2. Diagnostic gratuit</h3>
                  <p className="text-gray-700">
                    Vous recevez un score et une analyse détaillée — prescription possible, vices de forme,
                    pièces manquantes, axes de défense. Tout est gratuit, sans engagement.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                    <Users size={24} className="text-purple-700" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900">3. Avocat partenaire (option)</h3>
                  <p className="text-gray-700">
                    Si votre dossier le mérite, nous pouvons — avec votre accord — vous mettre en relation avec un
                    avocat partenaire spécialisé en droit du recouvrement.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Ce que nous ne sommes pas</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={24} className="text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-800 leading-relaxed mb-3">
                      <strong>Anti-Recouvrement n'est pas un cabinet d'avocats.</strong> Notre analyse vous
                      donne une lecture juridique de votre situation — elle ne constitue pas un avis juridique
                      au sens du Code de procédure civile et ne remplace pas la consultation d'un professionnel
                      du droit.
                    </p>
                    <p className="text-gray-800 leading-relaxed">
                      Pour toute décision contentieuse importante (contestation devant un juge, opposition à
                      saisie, négociation avec le créancier), nous vous recommandons fortement de consulter un
                      avocat — d'où nos partenariats.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Nos engagements</h2>
              <div className="space-y-4">
                {[
                  { icon: <Shield size={20} className="text-green-600" />, text: 'Confidentialité totale : vos documents ne sont jamais publiés. Conformité RGPD intégrale (consentement explicite à chaque étape).' },
                  { icon: <CheckCircle size={20} className="text-green-600" />, text: 'Analyse gratuite : aucune carte bancaire demandée, jamais. Notre modèle économique repose sur la mise en relation avec des avocats partenaires, pas sur votre paiement.' },
                  { icon: <Scale size={20} className="text-green-600" />, text: 'Transparence : nous vous disons quand votre dossier est solide, mais aussi quand il ne l\'est pas. Nous n\'inventons pas d\'angle de défense qui n\'existe pas.' },
                  { icon: <Brain size={20} className="text-green-600" />, text: 'IA encadrée : notre intelligence artificielle est calibrée pour rester humble — un score élevé ne garantit jamais une issue favorable, seule une procédure contradictoire le peut.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    {item.icon}
                    <p className="text-gray-700 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Qui sommes-nous ?</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Anti-Recouvrement est un projet porté par <strong>Mathieu Welsch</strong>, fondateur, et opéré par
                <strong> Sequana Tech</strong>. Le site combine une expertise produit/tech avec un partenariat
                permanent avec des avocats spécialisés en droit du recouvrement.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Le projet est basé en France, et notre service s'adresse exclusivement à des résidents français
                pour des créances relevant du droit français.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Une question ?</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Consultez notre <Link to="/blog" className="text-blue-700 underline hover:text-blue-900">blog</Link>
                {' '}— nous y publions régulièrement des guides détaillés sur la prescription des dettes (URSSAF,
                banques, impôts, CAF, etc.) et sur les courriers les plus courants des cabinets de recouvrement.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Pour les questions juridiques individuelles ou les demandes RGPD, consultez notre{' '}
                <Link to="/confidentialite" className="text-blue-700 underline hover:text-blue-900">politique de confidentialité</Link>
                {' '}ou contactez-nous directement.
              </p>
            </div>

            <div className="text-center pt-8">
              <Link to="/">
                <Button size="lg" className="px-8 py-6 text-lg font-semibold rounded-full" style={{ backgroundColor: '#1e3a8a', color: 'white' }}>
                  Lancer mon analyse gratuite
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;
